import { useRef } from 'react';
import { IconArrowLeftRegular, IconArrowRightRegular } from '@croquiscom-pvt/zds';
import { vars } from '@croquiscom-pvt/zds/tokens';
import styled from '@emotion/styled';
import { SwiperOptions } from 'swiper';

import { Navigation, Swiper, SwiperRef, SwiperSlide } from '@common/carousel/swiper';
import { SWIPER_STYLE } from '@common/styles';

import { useMediaQuery } from '../hooks/useMediaQuery';
import { ProductSwatchCard, ProductSwatchItem } from './ProductSwatchCard';

export interface ProductSwatchCarouselProps {
  className?: string;
  swiperOptions?: SwiperOptions;
  items: ProductSwatchItem[];
  onItemClick?: (item: ProductSwatchItem, position: number) => void;
  onItemImpression?: (item: ProductSwatchItem, position: number) => void;
}

const SLIDES_PER_VIEW = 'auto' as const;

const PREV_BUTTON_CLASS = 'product-swatch-carousel-prev-button';
const NEXT_BUTTON_CLASS = 'product-swatch-carousel-next-button';

export const ProductSwatchCarousel = ({
  className,
  swiperOptions,
  items,
  onItemClick,
  onItemImpression,
}: ProductSwatchCarouselProps) => {
  const shouldRenderNavigation = useMediaQuery('(min-width: 600px)');
  const ref = useRef<SwiperRef>(null);
  const totalCount = items.length;
  const lastItemIndex = totalCount - 1;

  const handlePrevClick = () => {
    if (ref.current) {
      const prevIndex = ref.current.swiper.activeIndex - 10;

      if (prevIndex < 0) {
        ref.current.swiper.slideTo(0);
        return;
      }

      ref.current.swiper.slideTo(prevIndex);
    }
  };

  const handleNextClick = () => {
    if (ref.current) {
      const nextIndex = ref.current.swiper.activeIndex + 10;

      if (nextIndex > lastItemIndex) {
        ref.current.swiper.slideTo(lastItemIndex);
        return;
      }

      ref.current.swiper.slideTo(nextIndex);
    }
  };

  return (
    <Container className={className}>
      <Carousel
        ref={ref}
        slidesPerView={SLIDES_PER_VIEW}
        spaceBetween={2}
        slidesOffsetBefore={16}
        slidesOffsetAfter={16}
        observer
        observeParents
        modules={[Navigation]}
        navigation={{
          prevEl: `.${PREV_BUTTON_CLASS}`,
          nextEl: `.${NEXT_BUTTON_CLASS}`,
        }}
        {...swiperOptions}
      >
        {items.map((item, index) => {
          return (
            <SwiperSlide key={`${item.id}-${index}`}>
              <ProductSwatchCard {...item} position={index} onClick={onItemClick} onImpression={onItemImpression} />
            </SwiperSlide>
          );
        })}

        {shouldRenderNavigation ? (
          <>
            <NavigationButtonWrapper className={PREV_BUTTON_CLASS} direction='left'>
              <NavigationButton type='button' aria-label='이전' onClick={handlePrevClick}>
                <IconArrowLeftRegular size={24} color='iconTertiary' />
              </NavigationButton>
            </NavigationButtonWrapper>

            <NavigationButtonWrapper className={NEXT_BUTTON_CLASS} direction='right'>
              <NavigationButton type='button' aria-label='다음' onClick={handleNextClick}>
                <IconArrowRightRegular size={24} color='iconTertiary' />
              </NavigationButton>
            </NavigationButtonWrapper>
          </>
        ) : null}
      </Carousel>
    </Container>
  );
};

const Container = styled.div`
  ${SWIPER_STYLE}
`;

const NavigationButtonWrapper = styled.div<{ direction: 'left' | 'right' }>`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 32px;
  display: none;
  align-items: center;
  z-index: 2;

  &.swiper-button-disabled,
  &.swiper-button-lock {
    display: none !important;
  }

  ${({ direction }) =>
    direction === 'left'
      ? `
          left: 10px;
          justify-content: flex-start;
        `
      : `
          right: 10px;
          justify-content: flex-end;
        `}
`;

const NavigationButton = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0.5px solid ${vars.color.borderPrimary};
  border-radius: 50%;
  background: ${vars.color.white};
  cursor: pointer;
  padding: 0;
`;

const Carousel = styled(Swiper)`
  .swiper-slide {
    position: relative;
    width: 48px !important;
    height: 58px !important;
    border-radius: 2px;
    overflow: hidden;

    &:after {
      position: absolute;
      display: block;
      content: '';
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      background: ${vars.color.thumbnailDimmed};
      z-index: 1;
      pointer-events: none;
    }

    &[data-selected='true'] {
      border: 1px solid ${vars.color.borderStrong};
    }

    &[data-dimmed='true']:after {
      background: ${vars.color.backgroundDimmed};
    }
  }

  &:hover ${NavigationButtonWrapper} {
    display: flex;
  }
`;
